/* eslint-disable max-lines-per-function */
import React from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";
import { DialogContent, Grid } from "@material-ui/core";
import { Modal, Button } from "components/haven-design";
import { DatePickerField, InputWithErrorLabel } from "components/haven-design/form/inputs";
import { useSnackbar } from "components/haven-design/snackbar";
import { DialogActions } from "components/haven-design/modal/dialog";
import { MoveInApplicants, MoveInApplicantsVariables } from "types/gql-types";
import { MOVE_IN_INTERESTS } from "./queries";
import styles from "./styles.module.css";

interface Props {
  isOpen: boolean;
  interestIds: string[];
  interestListId: string;
  onRequestClose: () => void
  onSuccess?: () => void
}

const FUTURE_DATE_ERROR = "Move in dates can't be in the future";

const FormSchema = Yup.object().shape({
  moveInDate: Yup.date()
    .nullable()
    .required("Required")
    .test("future_date", FUTURE_DATE_ERROR, (date) => {
      if (!date) return false;
      return date <= new Date();
    }),
  unitNumber: Yup.string(),
});

export const MoveInModal: React.FC<Props> = props => {
  const { openSnackbar } = useSnackbar();
  const [moveInInterest, { loading }] =
  useMutation<MoveInApplicants, MoveInApplicantsVariables>(MOVE_IN_INTERESTS);
  const suffix = props.interestIds.length > 1 ? `s (${props.interestIds.length})` : "";
  return (
    <Modal
      title={`Move In Applicant${suffix}`}
      isOpen={props.isOpen}
      onRequestClose={props.onRequestClose}
      className={styles.moveInModal} fullWidth
    >
      <Formik validationSchema={FormSchema} initialValues={{ moveInDate: new Date(), unitNumber: "" }} onSubmit={async (values) => {
        try {
          await moveInInterest({
            variables: {
              interestListIds: [props.interestListId],
              interestIds: props.interestIds,
              unitNumber: values.unitNumber || null,
              moveInDate: values.moveInDate.toISOString(),
            },
          });
          openSnackbar({ message: "Updated Status Successfully", status: "success" });
          if (props.onSuccess) {
            props.onSuccess();
          }
          props.onRequestClose();
        } catch (error) {
          openSnackbar({ message: "Error updating status", status: "error" });
        }
      }}>
        <Form>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePickerField id="move-in-date" fullWidth label="Move In Date *" name="moveInDate" small withError disableFuture />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputWithErrorLabel
                  id="move-in-unit-number"
                  label="Unit Number"
                  name="unitNumber"
                  type="text"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button id="move-in-cancel" color="secondary" size="L" onClick={props.onRequestClose}>Cancel</Button>
            <Button id="move-in-submit" color="primary" type="submit" size="L" disabled={loading}>Submit</Button>
          </DialogActions>
        </Form>
      </Formik>
    </Modal>
  );
};
